import { useEffect, useState, useMemo, useRef, useCallback } from 'react';

// MUI Theme
import { Button, Checkbox, Grid, IconButton, ListItemText, Tab, TextField } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

// Services
import AuthService from '../../../../services/auth.service';
import { updateTask } from '../../../../services/tasksQueue.service';

//Redux
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { addWatcher, removeWatcher } from '../../../../redux/slices/tasksQueueSlice';
import { SettingsState } from '../../../../redux/slices/settingsSlice';

// Utils
import { linkTo } from '../../../../utils';
import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';

// Types
import { AssignedTaskQueue, AssociatedResource } from '../../../../types/TasksQueue';
import { ResourceType, resourceTypeToPath } from '../../../../types/ResourceType';
import { AppPermissions } from '../../../../constants/Permissions';
import { TasksReaderQueue } from '../../../../constants/Constants';
import { UserProfile } from '../../../../types/UserProfile';
import { DataValues } from '../../../../types/Settings';

// Common Components
import { Spinner } from '../../../common/Spinner';
import { tablePrimaryButtonSx } from '../../../../styles/tableButtonStyles';

import CommentList from '../../comments/commentList';

// Styles
import '../../../../styles/muiTabs.scss';

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    width: '100%',
    padding: '16px 16px 0 16px',
  },
  titleLable: {
    margin: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#000000DE',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  desc: {
    margin: '24px 0',
  },
  message: {
    margin: 0,
    fontSize: '16px',
    color: '#000000DE',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
    '&:hover': {
      backgroundColor: '#f0f0f0',
      cursor: 'pointer',
    },
    padding: '8px',
    marginLeft: '-8px',
    borderRadius: '4px',
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  heading: {
    margin: 0,
    padding: '0 5px 0 0',
    fontWeight: 'bold',
  },
  text: {
    margin: 0,
  },
  moreDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    marginBottom: '24px',
    maxWidth: '550px',
    [theme.breakpoints.down('md')]: {
      flex: '1 1 calc(50% - 20px)',
    },

    [theme.breakpoints.down('sm')]: {
      flex: '1 1 100%',
    },
  },
  checkbox: {
    '& .MuiCheckbox-root': {
      color: 'grey',
    },
    '& .MuiCheckbox-root.Mui-checked': {
      color: 'green',
    },
  },
  autocomplete: {
    '& .MuiAutocomplete-root': {
      minWidth: '260px',
      maxWidth: '700px',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: '42px',
      padding: '4px 9px',
      minWidth: '260px',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '100px',
    },
    '& .MuiAutocomplete-inputRoot': {
      textAlign: 'left',
      minHeight: '42px',
    },
    '& .MuiInputBase-input': {
      fontSize: '13.12px',
    },
    '& .MuiAutocomplete-option': {
      fontSize: '13.12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiAutocomplete-paper': {
      width: '260px',
      maxWidth: '260px',
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'none',
      '-ms-overflow-style': 'none',
    },
    '& .MuiAutocomplete-paper::-webkit-scrollbar': {
      display: 'none',
    },
    '& .MuiOutlinedInput-adornedEnd .MuiInputBase-adornedEnd': {
      display: 'none',
    },
    '& .MuiAutocomplete-clearIndicator': {
      display: 'none',
    },
  },
  customList: {
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
  },
  watchersContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '16px',
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    visibility: 'hidden',
  },
  iconVisible: {
    opacity: 1,
    visibility: 'visible',
  },
  icontext: {
    fontSize: '15px',
    paddingLeft: '2px',
    lineHeight: '17px',
  },
  tabPanel: { height: '100vh', display: 'contents' },
}));

const TasksQueueDetails = ({ selectedRowData, onWatchersUpdate, settingStates }) => {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isWatching, setIsWatching] = useState(false);
  const [activeBottomTab, setBottomActiveTab] = useState('1');
  const [messageState, setMessageState] = useState({
    current: '',
    original: '',
    isEditing: false,
  });

  const tasksQueueState = useAppSelector((state) => state.tasksQueue);
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const messageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedRowData?.watchingUsers) {
      setSelectedUsers(selectedRowData.watchingUsers.map((user) => user.id));
    }
    setMessageState((prevState) => ({
      ...prevState,
      current: selectedRowData?.message ?? '',
      original: selectedRowData?.message ?? '',
    }));
  }, [selectedRowData]);

  useEffect(() => {
    AuthService.getUserProfile().then((profile) => {
      setUserProfile(profile);
      setIsWatching(profile ? selectedUsers.includes(profile.UserId) : false);
    });
  }, [selectedUsers]);

  useEffect(() => {
    if (messageState.isEditing && messageRef.current) {
      messageRef.current.focus();
    }
  }, [messageState.isEditing]);

  const options = useMemo(
    () => tasksQueueState.assignableUsers.filter((user) => user.label).sort((a, b) => a.label.localeCompare(b.label)),
    [tasksQueueState.assignableUsers],
  );

  const handleBottomTabchange = useCallback((event: React.SyntheticEvent, newValue: string) => setBottomActiveTab(newValue), []);

  const handleWatchersChange = async (event: any, value: any) => {
    const newSelectedIds = value.map((user: any) => user.id);
    const addedUsers = newSelectedIds.filter((id) => !selectedUsers.includes(id));
    const removedUsers = selectedUsers.filter((id) => !newSelectedIds.includes(id));

    setSelectedUsers(newSelectedIds);

    await Promise.all([
      ...addedUsers.map((userId) => dispatch(addWatcher({ taskId: selectedRowData.id, userId }))),
      ...removedUsers.map((userId) => dispatch(removeWatcher({ taskId: selectedRowData.id, userId }))),
    ]);

    onWatchersUpdate?.();
  };

  const toggleWatch = async () => {
    if (userProfile) {
      if (isWatching) {
        await dispatch(removeWatcher({ taskId: selectedRowData.id, userId: userProfile.UserId }));
      } else {
        await dispatch(addWatcher({ taskId: selectedRowData.id, userId: userProfile.UserId }));
      }
      setIsWatching((prev) => !prev);
    }
    onWatchersUpdate?.();
  };

  const handleEditClick = () => {
    setMessageState((prevState) => ({
      ...prevState,
      isEditing: true,
    }));
  };

  const handleSaveMessage = async () => {
    if (selectedRowData) {
      const request = {
        tasks: [
          {
            id: selectedRowData.id,
            associatedResources: selectedRowData.associatedResources,
            assignedUserId: selectedRowData.assignedUserId,
            category: selectedRowData.category,
            displayName: selectedRowData.displayName,
            message: messageState.current,
            severity: selectedRowData.severity,
            status: selectedRowData.status,
            title: selectedRowData.Title,
            watchingUsers: selectedRowData.watchingUsers,
          },
        ],
      };

      await updateTask(request);
      setMessageState((prevState) => ({
        ...prevState,
        original: prevState.current,
        isEditing: false,
      }));
      onWatchersUpdate?.();
    }
  };

  const handleCancelEdit = () => {
    setMessageState((prevState) => ({
      ...prevState,
      current: prevState.original,
      isEditing: false,
    }));
  };

  if (!selectedRowData) return null;

  const rowData: AssignedTaskQueue = selectedRowData;
  const primaryResource = rowData.associatedResources.find((resource) => resource.isPrimary);
  const { taskStatus, taskSeverity } = settingStates as SettingsState;

  return (
    <>
      {tasksQueueState.areTasksLoading && <Spinner loading={tasksQueueState.areTasksLoading ?? false} />}
      <div className={classes.container}>
        {rowData.title && (
          <>
            <h2 className={classes.titleLable}>Title</h2>
            <p className={classes.title}>{rowData.title ?? ''}</p>
          </>
        )}
        {rowData.message && (
          <div className={classes.desc}>
            <h3 className={classes.titleLable}>Message</h3>
            {messageState.isEditing && AuthService.hasPermission(AppPermissions.TasksQueue.Edit) ? (
              <div>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={messageState.current}
                  onChange={(e) =>
                    setMessageState((prevState) => ({
                      ...prevState,
                      current: e.target.value,
                    }))
                  }
                  inputRef={messageRef}
                />
                <div className={`${classes.iconContainer} ${messageState.isEditing ? classes.iconVisible : ''}`}>
                  <IconButton onClick={handleSaveMessage} color='primary' aria-label='save' sx={{ ...tablePrimaryButtonSx, width: 'auto' }}>
                    <CheckIcon />
                    <span className={classes.icontext}>Save</span>
                  </IconButton>
                  <IconButton onClick={handleCancelEdit} color='secondary' aria-label='cancel'>
                    <CloseIcon />
                    <span className={classes.icontext}>Cancel</span>
                  </IconButton>
                </div>
              </div>
            ) : (
              <p className={classes.message} onClick={handleEditClick}>
                {messageState.current || 'Click to add a message...'}
              </p>
            )}
          </div>
        )}
        {rowData.displayName && primaryResource && (
          <div className={classes.detailRow} style={{ marginBottom: 10 }}>
            <h4 className={classes.heading}>Reference:</h4>
            {linkTo(resourceTypeToPath[primaryResource.resourceType], `${primaryResource.subjectId}`, rowData.displayName, '_blank')}
          </div>
        )}
        <div className={classes.moreDetails} style={{ marginBottom: '12px' }}>
          {primaryResource && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Type:</h4>
              <p className={classes.text}>{ResourceType[primaryResource.resourceType]}</p>
            </div>
          )}
          {rowData.category && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Category:</h4>
              <p className={classes.text}>{rowData.category}</p>
            </div>
          )}
          {rowData.associatedResources.length > 0 && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Status:</h4>
              <p className={classes.text}>{taskStatus?.value.find((v: DataValues) => parseInt(v.text) === rowData.status)?.value ?? ''}</p>
            </div>
          )}
          {rowData.severity && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Severity:</h4>
              <p className={classes.text}>{taskSeverity?.value.find((item) => item.text === rowData.severity)?.value || ''}</p>
            </div>
          )}
        </div>

        <div className={classes.moreDetails}>
          {rowData.assignedUserName && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Assigned To:</h4>
              <p className={classes.text}>{rowData.assignedUserName}</p>
            </div>
          )}
          {rowData.age && (
            <div className={classes.detailRow}>
              <h4 className={classes.heading}>Age:</h4>
              <p className={classes.text}>{rowData.age}</p>
            </div>
          )}
          <div className={classes.detailRow}>
            <h4 className={classes.heading}>{withTimeMode('Created At')}:</h4>
            <p className={classes.text}>{toStandardDate(rowData.createdDateTime) ?? ''}</p>
          </div>
          <div className={classes.detailRow}>
            <h4 className={classes.heading}>{withTimeMode('Modified At')}:</h4>
            <p className={classes.text}>{toStandardDate(rowData.modifiedDateTime) ?? ''}</p>
          </div>
        </div>
      </div>
      {AuthService.hasPermission(AppPermissions.TasksQueue.Edit) && (
        <div className={classes.watchersContainer}>
          <h4 className={classes.heading}>Watchers:</h4>
          <Autocomplete
            multiple
            id='watchers-autocomplete'
            className={classes.autocomplete}
            options={options}
            getOptionLabel={(option) => option.label || ''}
            value={options.filter((option) => selectedUsers.includes(option.id))}
            onChange={handleWatchersChange}
            renderInput={(params) => <TextField {...params} variant='outlined' placeholder='Add Watchers...' />}
            disableCloseOnSelect
            classes={{ paper: classes.customList }}
            renderOption={(props, option) => (
              <>
                {props['data-option-index'] === 0 && (
                  <Button onClick={toggleWatch} style={{ paddingLeft: '12px' }}>
                    {isWatching ? TasksReaderQueue.StopWatching : TasksReaderQueue.WatchThis}
                  </Button>
                )}
                <li {...props} style={{ padding: 0 }}>
                  <Checkbox checked={selectedUsers.includes(option.id)} className={classes.checkbox} color='primary' />
                  <ListItemText primary={option.label} />
                </li>
              </>
            )}
          />
        </div>
      )}
      <Grid item xs={12}>
        <TabContext value={activeBottomTab}>
          <div className='app-tabs'>
            <TabList onChange={handleBottomTabchange} aria-label='comments tab'>
              {AuthService.hasPermission(AppPermissions.Comment.Read) && <Tab label='Comments' value='1' sx={{ textTransform: 'none' }} />}
            </TabList>
          </div>
          {AuthService.hasPermission(AppPermissions.Comment.Read) && (
            <TabPanel value='1' className={classes.tabPanel}>
              {selectedRowData && selectedRowData.associatedResources && (
                <CommentList
                  resourceType={ResourceType.Task}
                  subjectId={selectedRowData.id}
                  secondarySubjects={selectedRowData.associatedResources
                    .filter((resource: AssociatedResource) => resource.isPrimary)
                    .map((resource: AssociatedResource) => ({
                      resourceType: resource.resourceType,
                      subjectId: resource.subjectId,
                    }))}
                />
              )}
            </TabPanel>
          )}
        </TabContext>
      </Grid>
    </>
  );
};

export default TasksQueueDetails;
